<template>
  <Layout
    :error-message="displayError ? errorMessage : ''"
    :is-loading="(isLoading && !errorMessage) || hasRedirectUrl"
  >
    <fragment>
      <v-form
        v-if="!isLoading && displayForm"
        ref="form"
        class="form-wrapper"
        @submit.prevent="startTracking"
      >
        <div
          v-if="displayAlreadyActiveWarning"
          class="already-active-warning warning"
        >
          This label is already active since {{ getTimeAgo(nbIotData.activatedAt) }}.
        </div>

        <div class="inputs-container">
          <div class="input-title">
            Tracking Reference
          </div>
          <v-text-field
            v-model="form.name"
            name="name"
            placeholder="Enter a tracking reference"
            hint="Enter a name or tracking number"
            :rules="[required('Tracking Reference')]"
            :disabled="isLinkingLabel"
            required
            clearable
            autocomplete="off"
          />
        </div>
        <div class="button-container">
          <v-btn
            large
            :loading="isLinkingLabel"
            depressed
            dark
            @click="startTracking"
          >
            Start tracking
          </v-btn>
        </div>
      </v-form>
      <div
        v-if="isLabelTerminated"
        class="terminated-warning warning"
      >
        This label has been terminated and can no longer be activated.
      </div>
      <div
        v-else-if="!isLoading && !displayForm && !hasActivationData && !hasRedirectUrl"
        class="use-nfc-message"
      >
        LABEL NOT YET ACTIVATED
        <br><br>
        TO ACTIVATE: TAP LABEL WITH NFC-ENABLED SMARTPHONE
      </div>
    </fragment>
  </Layout>
</template>
<script>
import { routeNames } from '../../constants';
import Layout from '../../components/organisms/layout/nb-iot-layout';
import rules from '../../mixins/input-validation-rules';
import dateTimeHelper from '../../mixins/date-time-helper';

function shouldIncludeTokenInUrl(urlHost) {
  // if host is reelables.com or localhost, include token in URL for easier testing and demoing
  return urlHost.includes('reelables.com') || urlHost.includes('localhost');
}

export default {
  name: 'NbIotLink',
  components: {
    Layout,
  },
  mixins: [
    rules,
    dateTimeHelper,
  ],
  props: {
    b: { type: String, required: true },
    t: { type: String, default: '' },
    n: { type: String, default: '' },
    hash: { type: String, default: '' },
  },
  data() {
    return {
      isLoading: true,
      canLinkLabel: false,
      isLinkingLabel: false,
      form: {
        name: '',
      },
    };
  },
  computed: {
    bleId() { return this.b; },
    token() { return this.t; },
    errorMessage() { return this.$store.state.nbIot.errorMessage; },
    isLoadingData() { return this.$store.state.nbIot.isLoading; },
    nbIotData() { return this.$store.state.nbIot.data; },
    hasData() {
      return !this.errorMessage && !this.isLoadingData && !!this.nbIotData;
    },
    requiresActivationData() {
      return !!this.nbIotData && this.nbIotData.requiresActivationData;
    },
    isAssetNotFoundError() { return this.errorMessage === `No asset found with bleId: ${this.bleId}`; },
    activationData() { return this.$store.state.nbIot.activationData; },
    hasActivationData() {
      return !this.errorMessage
        && !this.isLinkingLabel && !!this.activationData;
    },
    displayForm() {
      return (this.isNfcTap || this.requiresActivationData)
        && !this.isLabelTerminated && !this.hasRedirectUrl;
    },
    isQrCode() { return this.hash && this.hash === '#qr'; },
    isNfcTap() { return !this.isQrCode; },
    displayAlreadyActiveWarning() {
      // already active - at least 1 hour ago
      return this.hasData
        && this.nbIotData.activatedAt
        && new Date(this.nbIotData.activatedAt) <= new Date(Date.now() - 60 * 60 * 1000);
    },
    isLabelTerminated() {
      return this.errorMessage.includes('Label has been terminated');
    },
    displayError() {
      return this.errorMessage && !this.isAssetNotFoundError && !this.isLabelTerminated;
    },
    redirectUrl() {
      return this.nbIotData && this.nbIotData.labelActivationRedirectUrl
        ? this.nbIotData.labelActivationRedirectUrl : null;
    },
    hasRedirectUrl() {
      return !!this.redirectUrl;
    },
  },
  watch: {
    hasData() {
      if (this.hasData && !this.requiresActivationData) {
        if (!this.hasRedirectUrl) {
        // 1) On created, use bleId and token to get nbIot data
        //      - if data - then route to tracking screen
          this.$router.replace({
            name: routeNames.nbIot.detail,
            query: { t: this.token, bleId: this.bleId, assetId: this.nbIotData.id },
          });
        }
      }
    },
    isLoadingData() {
      if ((!this.isLoadingData && this.isAssetNotFoundError) || this.requiresActivationData) {
        // 2) If no data - then label isn't linked
        //    - if qrCode is truthy then display NFC Tap prompt
        //    - display form and ask user to input Tracking Reference
        this.isLoading = false;
      }
    },
    hasActivationData() {
      if (this.hasActivationData) {
        if (this.activationData.token) {
          // Route user to tracking screen on successful activation
          this.$router.replace({
            name: routeNames.nbIot.detail,
            query: { t: this.activationData.token, assetId: this.activationData.assetId },
          });
        } else {
          this.$router.replace({
            name: routeNames.nbIot.assetCreated,
            query: { assetId: this.activationData.assetId, assetName: this.form.name },
            params: { validRoute: true },
          });
        }
      }
    },
    hasRedirectUrl() {
      if (this.hasRedirectUrl) {
        // set query string to a = assetId
        const url = new URL(this.redirectUrl);

        let status = 0;

        if (!this.requiresActivationData) {
          status = 1001;
        }
        url.searchParams.set('status', status);
        url.searchParams.set('asset_id', this.nbIotData.id);
        url.searchParams.set('label_id', this.nbIotData.bleId);

        if (this.displayAlreadyActiveWarning) {
          url.searchParams.set('active_since', this.nbIotData.activatedAt);
        }

        if (shouldIncludeTokenInUrl(url.host)) {
          url.searchParams.set('t', this.token);
        }
        window.location.href = url.toString();
      }
    },
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      this.$store.dispatch(
        'nbIot/getNbIotAsset',
        {
          token: this.token,
          bleId: this.bleId,
          createIfNotExists: this.isNfcTap,
          forceLoading: true,
        },
      );
    },
    async startTracking() {
      if (this.$refs.form.validate()) {
        this.isLinkingLabel = true;

        if (this.$root.featureToggles.useUserLocation() && navigator.geolocation) {
          // With location...
          navigator.geolocation.getCurrentPosition(async (position) => {
            await this.$store.dispatch(
              'nbIot/activateNbIotLabel',
              {
                token: this.token,
                name: this.form.name,
                bleId: this.bleId,
                location: {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  error: position.coords.accuracy,
                  timestamp: new Date(position.timestamp).toISOString(),
                },
              },
            );
            this.isLinkingLabel = false;
          }, () => {
            // error
            // console.log('** ERROR GETTING LOCATION:', error.message);
            this.isLinkingLabel = false;
          });
        } else {
          // No location...
          await this.$store.dispatch(
            'nbIot/activateNbIotLabel',
            { token: this.token, name: this.form.name, bleId: this.bleId },
          );
          this.isLinkingLabel = false;
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/_updated-variables.scss';

.form-wrapper {
  padding: 20px;
  display: flex;
  flex-direction: column;
}
.warning {
  background-color: $gray2;
  color: $white;
  font-weight: bold;
  padding: 10px;
  margin-bottom: 20px;
  text-align: center;
}
.terminated-warning {
  background-color: $red;
  margin-top: 20px;
}
.already-active-warning {
  background-color: $orange;
}
.inputs-container {
  margin-bottom: 20px;
  color: $gray1;
  font-size: 1.25rem;
  word-wrap: break-word;
}
.input-title {
  font-size: 1rem;
  text-transform: uppercase;
  color: $gray3;
  font-weight: bold;
  margin-bottom: 8px;
}
.button-container {
  display: flex;
  flex-direction: column;
}
.use-nfc-message {
  padding: 50px;
  display: flex;
  flex-direction: column;
  text-align: center;
  line-height: 28px;
}
</style>
<style lang="scss">
.inputs-container .error--text .v-messages__message {
  color: #ff3e3e;
}
</style>
